import type { I18n } from "@lingui/core";

/**
 * Translated messages for zod checks (#2235). Each message says what to enter.
 *
 * A check without a message of its own shows zod's built-in English text in
 * every locale. Pass one of these as the check's params instead:
 *
 *   z.string().max(30, checkMessage.maxLength(i18n))
 *   z.number().int(checkMessage.wholeNumber(i18n)).min(0, checkMessage.range(i18n, 0, 365))
 *
 * The `error` callbacks run at parse time, so the bound values come from the
 * check itself and the text follows the locale of the `i18n` passed in. The
 * `src/lib/__tests__/zodMessages.guard.test.ts` guard fails on a check without
 * a message or with a hard-coded English one.
 */

type Bounded = { minimum?: number | bigint; maximum?: number | bigint };

const num = (value: number | bigint | undefined): number | undefined =>
  typeof value === "bigint" ? Number(value) : value;

const required = (i18n: I18n) =>
  i18n._({ id: "validation.check.required", message: "Required" });

const maxLength = (i18n: I18n, max: number | undefined) =>
  i18n._({ id: "validation.check.maxLength", message: "Up to {max} characters", values: { max } });

const minLength = (i18n: I18n, min: number | undefined) =>
  i18n._({ id: "validation.check.minLength", message: "At least {min} characters", values: { min } });

const numberMin = (i18n: I18n, min: number | undefined) =>
  i18n._({ id: "validation.check.min", message: "Enter {min} or more", values: { min } });

const numberMax = (i18n: I18n, max: number | undefined) =>
  i18n._({ id: "validation.check.max", message: "Enter {max} or less", values: { max } });

const wholeNumber = (i18n: I18n) =>
  i18n._({ id: "validation.check.wholeNumber", message: "Enter a whole number" });

const number = (i18n: I18n) =>
  i18n._({ id: "validation.check.number", message: "Enter a number" });

const selectAtLeast = (i18n: I18n, min: number | undefined) =>
  min === 1
    ? i18n._({ id: "validation.check.selectAtLeastOne", message: "Select at least one" })
    : i18n._({ id: "validation.check.selectAtLeast", message: "Select at least {min}", values: { min } });

const selectUpTo = (i18n: I18n, max: number | undefined) =>
  i18n._({ id: "validation.check.selectUpTo", message: "Select up to {max}", values: { max } });

const email = (i18n: I18n) =>
  i18n._({ id: "validation.check.emailAddress", message: "Enter an email such as name@example.com" });

const url = (i18n: I18n) =>
  i18n._({ id: "validation.check.webAddress", message: "Start the web address with https://" });

const time = (i18n: I18n) =>
  i18n._({ id: "validation.check.timeOfDay", message: "Enter a time such as 07:30" });

const isoDate = (i18n: I18n) =>
  i18n._({ id: "validation.check.isoDate", message: "Enter a date as YYYY-MM-DD" });

const date = (i18n: I18n) =>
  i18n._({ id: "validation.check.date", message: "Enter a valid date" });

const option = (i18n: I18n) =>
  i18n._({ id: "validation.check.option", message: "Choose one of the options" });

const multipleOf = (i18n: I18n, divisor: number | undefined) =>
  i18n._({ id: "validation.check.multipleOf", message: "Enter a multiple of {divisor}", values: { divisor } });

const valid = (i18n: I18n) =>
  i18n._({ id: "validation.check.valid", message: "Enter a valid value" });

const range = (i18n: I18n, min: number, max: number) =>
  i18n._({ id: "validation.check.range", message: "Enter a number from {min} to {max}", values: { min, max } });

const wholeRange = (i18n: I18n, min: number, max: number) =>
  i18n._({ id: "validation.check.wholeRange", message: "Enter a whole number from {min} to {max}", values: { min, max } });

const amountRange = (i18n: I18n, min: number, max: number) =>
  i18n._({ id: "validation.check.amountChfRange", message: "Enter an amount from CHF {min} to {max}", values: { min, max } });

const percentRange = (i18n: I18n, min: number, max: number) =>
  i18n._({ id: "validation.check.percentRange", message: "Enter a percentage from {min} to {max}", values: { min, max } });

/** Params for zod checks: `{ error }` callbacks resolved with `i18n` at parse time. */
export const checkMessage = {
  /** `.min(1)` on a text, or any check whose only failure is an empty value. */
  required: (i18n: I18n) => ({ error: () => required(i18n) }),
  /** `.max(n)` on a string: "Up to n characters". */
  maxLength: (i18n: I18n) => ({ error: (issue: Bounded) => maxLength(i18n, num(issue.maximum)) }),
  /** `.min(n)` (n > 1) on a string: "At least n characters". */
  minLength: (i18n: I18n) => ({ error: (issue: Bounded) => minLength(i18n, num(issue.minimum)) }),
  /** `.min(n)` / `.gte(n)` on a number: "Enter n or more". */
  min: (i18n: I18n) => ({ error: (issue: Bounded) => numberMin(i18n, num(issue.minimum)) }),
  /** `.max(n)` / `.lte(n)` on a number: "Enter n or less". */
  max: (i18n: I18n) => ({ error: (issue: Bounded) => numberMax(i18n, num(issue.maximum)) }),
  /** `.int()`. */
  wholeNumber: (i18n: I18n) => ({ error: () => wholeNumber(i18n) }),
  /** All checks of a bounded number field: "Enter a number from min to max". */
  range: (i18n: I18n, min: number, max: number) => ({ error: () => range(i18n, min, max) }),
  /** All checks of a bounded integer field: "Enter a whole number from min to max". */
  wholeRange: (i18n: I18n, min: number, max: number) => ({ error: () => wholeRange(i18n, min, max) }),
  /** Money stored in Rappen, entered in francs: pass the bounds in CHF. */
  amountRange: (i18n: I18n, minChf: number, maxChf: number) => ({ error: () => amountRange(i18n, minChf, maxChf) }),
  /** A fraction stored as 0..1 and entered as a percentage: pass the bounds in percent. */
  percentRange: (i18n: I18n, min: number, max: number) => ({ error: () => percentRange(i18n, min, max) }),
  /** `.min(n)` on an array: "Select at least one" / "Select at least n". */
  selectAtLeast: (i18n: I18n) => ({ error: (issue: Bounded) => selectAtLeast(i18n, num(issue.minimum)) }),
  /** `.max(n)` on an array: "Select up to n". */
  selectUpTo: (i18n: I18n) => ({ error: (issue: Bounded) => selectUpTo(i18n, num(issue.maximum)) }),
  email: (i18n: I18n) => ({ error: () => email(i18n) }),
  url: (i18n: I18n) => ({ error: () => url(i18n) }),
  /** A `HH:MM` regex. */
  time: (i18n: I18n) => ({ error: () => time(i18n) }),
  /** A `YYYY-MM-DD` regex or `.datetime()`. */
  isoDate: (i18n: I18n) => ({ error: () => isoDate(i18n) }),
};

type FallbackIssue = Bounded & {
  code?: string;
  input?: unknown;
  origin?: string;
  format?: string;
  expected?: string;
  divisor?: number | bigint;
};

/**
 * The message for an issue no schema-level message answered (`z.config`
 * customError, `zodConfig.ts`). Covers every issue code, so zod's built-in
 * English never reaches a form; a check's own message still wins.
 */
export function fallbackMessage(i18n: I18n, issue: FallbackIssue): string {
  if (issue.input === undefined && (issue.code === "invalid_type" || issue.code === "invalid_value")) {
    return required(i18n);
  }
  const collection = issue.origin === "array" || issue.origin === "set";
  switch (issue.code) {
    case "too_big":
      if (issue.origin === "string") return maxLength(i18n, num(issue.maximum));
      if (collection) return selectUpTo(i18n, num(issue.maximum));
      if (issue.origin === "number" || issue.origin === "int" || issue.origin === "bigint") {
        return numberMax(i18n, num(issue.maximum));
      }
      return issue.origin === "date" ? date(i18n) : valid(i18n);
    case "too_small":
      if (issue.origin === "string") {
        return num(issue.minimum) === 1 ? required(i18n) : minLength(i18n, num(issue.minimum));
      }
      if (collection) return selectAtLeast(i18n, num(issue.minimum));
      if (issue.origin === "number" || issue.origin === "int" || issue.origin === "bigint") {
        return numberMin(i18n, num(issue.minimum));
      }
      return issue.origin === "date" ? date(i18n) : valid(i18n);
    case "invalid_format":
      if (issue.format === "email") return email(i18n);
      if (issue.format === "url") return url(i18n);
      if (issue.format === "date" || issue.format === "datetime") return isoDate(i18n);
      if (issue.format === "time") return time(i18n);
      return valid(i18n);
    case "invalid_type":
      if (issue.expected === "int") return wholeNumber(i18n);
      if (issue.expected === "number" || issue.expected === "bigint") return number(i18n);
      if (issue.expected === "date") return date(i18n);
      return valid(i18n);
    case "not_multiple_of":
      return multipleOf(i18n, num(issue.divisor));
    case "invalid_value":
      return option(i18n);
    default:
      return valid(i18n);
  }
}
